import axios from 'axios'
import { apiUrl } from '../config'
import authenticationStore from '../stores/authenticationStore'

const getAuthHeaders = () => ({
  Authorization: `Bearer ${
    JSON.parse(authenticationStore.appToken).access_token
  }`,
  'Content-Type': 'application/json',
})

const getMultipartHeaders = () => ({
  Authorization: `Bearer ${
    JSON.parse(authenticationStore.appToken).access_token
  }`,
  'Content-Type': 'multipart/form-data',
})

export const NewsFeedRequest = {
  // ==========================================
  // BÀI VIẾT (Posts)
  // ==========================================

  /** Lấy danh sách feed bài viết (phân trang) */
  getFeed: (page = 0, size = 10) =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/posts?page=${page}&size=${size}`,
      headers: getAuthHeaders(),
    }),

  /** Lấy chi tiết 1 bài viết */
  getPostById: postId =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}`,
      headers: getAuthHeaders(),
    }),

  /** Tạo bài viết mới (multipart: text + files) */
  createPost: formData =>
    axios({
      method: 'post',
      url: `${apiUrl}/api/v1/newsfeed/posts`,
      headers: getMultipartHeaders(),
      data: formData,
      onUploadProgress: progressEvent => {
        // Có thể dùng để hiển thị progress bar
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )
        if (window.__uploadProgressCallback) {
          window.__uploadProgressCallback(percentCompleted)
        }
      },
    }),

  /** Cập nhật bài viết (multipart: text + files) */
  updatePost: (postId, formData) =>
    axios({
      method: 'put',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}`,
      headers: getMultipartHeaders(),
      data: formData,
    }),

  /** Xóa bài viết (soft delete) */
  deletePost: postId =>
    axios({
      method: 'delete',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}`,
      headers: getAuthHeaders(),
    }),

  /** Ẩn bài viết */
  hidePost: postId =>
    axios({
      method: 'patch',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}/hide`,
      headers: getAuthHeaders(),
    }),

  // ==========================================
  // LIKE BÀI VIẾT
  // ==========================================

  /** Toggle like/unlike bài viết (optional reaction) */
  toggleLikePost: (postId, reaction) =>
    axios({
      method: 'post',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}/like`,
      headers: getAuthHeaders(),
      data: reaction ? { reaction } : undefined,
    }),

  /** Toggle bookmark bài viết */
  toggleBookmarkPost: postId =>
    axios({
      method: 'post',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}/bookmark`,
      headers: getAuthHeaders(),
    }),

  /** Lấy danh sách bài viết đã lưu (bookmark) */
  getBookmarkedFeed: (page = 0, size = 10) =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/posts/bookmarked?page=${page}&size=${size}`,
      headers: getAuthHeaders(),
    }),

  /** Kiểm tra user đã like bài viết chưa */
  checkLikedPost: postId =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}/like/check`,
      headers: getAuthHeaders(),
    }),

  /** Lấy danh sách người đã like bài viết */
  getLikesPost: postId =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}/likes`,
      headers: getAuthHeaders(),
    }),

  /** Lấy danh sách media của tôi */
  getMyMedia: () =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/posts/media`,
      headers: getAuthHeaders(),
    }),

  // ==========================================
  // COMMENT
  // ==========================================

  /** Lấy danh sách comment root (phân trang). mediaId: comment của ảnh cụ thể */
  getComments: (postId, page = 0, size = 5, mediaId = null) => {
    const mediaQuery = mediaId != null ? `&mediaId=${mediaId}` : ''
    return axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}/comments?page=${page}&size=${size}${mediaQuery}`,
      headers: getAuthHeaders(),
    })
  },

  /** Owner: cập nhật caption của 1 media */
  updateMediaCaption: (postId, mediaId, caption) =>
    axios({
      method: 'patch',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}/media/${mediaId}/caption`,
      headers: getAuthHeaders(),
      data: { caption: caption || '' },
    }),

  /** Lấy danh sách replies cho 1 comment */
  getReplies: (commentId, page = 0, size = 3) =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/comments/${commentId}/replies?page=${page}&size=${size}`,
      headers: getAuthHeaders(),
    }),

  /** Tạo comment mới (hỗ trợ file đính kèm) */
  createComment: (postId, data) => {
    // Nếu data là FormData (có file đính kèm), dùng multipart header
    const isFormData = data instanceof FormData
    return axios({
      method: 'post',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}/comments`,
      headers: isFormData ? getMultipartHeaders() : getAuthHeaders(),
      data,
    })
  },

  /** Xóa comment */
  deleteComment: commentId =>
    axios({
      method: 'delete',
      url: `${apiUrl}/api/v1/newsfeed/comments/${commentId}`,
      headers: getAuthHeaders(),
    }),

  /** Toggle like/unlike comment (optional reaction) */
  toggleLikeComment: (commentId, reaction) =>
    axios({
      method: 'post',
      url: `${apiUrl}/api/v1/newsfeed/comments/${commentId}/like`,
      headers: getAuthHeaders(),
      data: reaction ? { reaction } : undefined,
    }),

  /** Lấy danh sách người đã like comment */
  getLikesComment: commentId =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/comments/${commentId}/likes`,
      headers: getAuthHeaders(),
    }),

  // ==========================================
  // THÔNG BÁO
  // ==========================================

  /** Lấy danh sách thông báo */
  getNotifications: (page = 0, size = 20) =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/users/curent-user/newsfeed-notifications?page=${page}&size=${size}`,
      headers: getAuthHeaders(),
    }),

  /** Đếm thông báo chưa đọc */
  getUnreadCount: () =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/users/curent-user/newsfeed-notifications?page=0&size=1`,
      headers: getAuthHeaders(),
    }),

  /** Đánh dấu tất cả thông báo đã đọc */
  markAllRead: () =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/users/curent-user/newsfeed-notifications/mark-all-as-read`,
      headers: getAuthHeaders(),
    }),

  /** Đánh dấu 1 thông báo đã đọc */
  markRead: notificationId =>
    axios({
      method: 'patch',
      url: `${apiUrl}/api/v1/users/curent-user/notifications/${notificationId}/read`,
      headers: getAuthHeaders(),
    }),

  /** Lấy danh sách bài viết đã ẩn */
  getHiddenFeed: (page = 0, size = 10) =>
    axios({
      method: 'get',
      url: `${apiUrl}/api/v1/newsfeed/posts/hidden?page=${page}&size=${size}`,
      headers: getAuthHeaders(),
    }),

  /** Mở lại bài viết đã ẩn */
  unhidePost: postId =>
    axios({
      method: 'patch',
      url: `${apiUrl}/api/v1/newsfeed/posts/${postId}/unhide`,
      headers: getAuthHeaders(),
    }),

  // ==========================================
  // SSE (Server-Sent Events) cho real-time comments
  // ==========================================

  /** Tạo SSE connection cho real-time comments theo post */
  createCommentSSE: postId => {
    const token = JSON.parse(authenticationStore.appToken).access_token
    const eventSource = new EventSource(
      `${apiUrl}/api/v1/newsfeed/sse/comments/${postId}?token=${token}`
    )
    return eventSource
  },
}
